import type { TariffSummary } from '../api/types'
import type { PreviewSlot } from '../lib/schedule'
import { fmtDay, fmtTime, tariffBand } from '../lib/schedule'

interface Props {
  slots: PreviewSlot[]
  tariff: TariffSummary | null
  showBefore: boolean
}

const ACTION_LABEL = { CHARGE: 'Charge', HOLD: 'Hold', DISCHARGE: 'Use battery' } as const

/**
 * 48 hours of half-hour cells coloured by planned action, with the tariff
 * band beneath. Customer windows are outlined; a draft being edited is
 * outlined dashed, and with showBefore the planned row appears above it.
 */
export function ScheduleTimeline({ slots, tariff, showBefore }: Props) {
  if (slots.length === 0) {
    return <p className="muted">The plan for the next 48 hours isn't available yet. Please check back in a few minutes.</p>
  }

  const rows: { key: string; label: string; cellAction: (s: PreviewSlot) => string }[] = showBefore
    ? [
        { key: 'before', label: 'Planned', cellAction: (s) => s.before },
        { key: 'after', label: 'With window', cellAction: (s) => s.a },
      ]
    : [{ key: 'plan', label: 'Plan', cellAction: (s) => s.a }]

  return (
    <div className="timeline" aria-label="Battery plan for the next 48 hours">
      <div className="timeline-scroll">
        <div className="timeline-grid" style={{ gridTemplateColumns: `6.5rem repeat(${slots.length}, minmax(10px, 1fr))` }}>
          <div className="timeline-rowlabel" />
          {(() => {
            // Day labels are wide: keep hour marks at least 8 cells (4h) clear of them.
            let lastDay = -100
            return slots.map((s, i) => {
              const d = new Date(s.t)
              const hh = fmtTime(d)
              const newDay = i === 0 || hh === '00:00'
              if (newDay) lastDay = i
              const hourMark = !newDay && i - lastDay >= 8 && hh.endsWith(':00') && Number(hh.slice(0, 2)) % 6 === 0
              return (
                <div key={`h${s.t}`} className={`timeline-hour${newDay ? ' timeline-newday' : ''}`}>
                  {newDay ? fmtDay(d) : hourMark ? hh.slice(0, 2) : ''}
                </div>
              )
            })
          })()}

          {rows.map((row) => (
            <Row key={row.key} label={row.label} slots={slots} actionOf={row.cellAction} />
          ))}

          <div className="timeline-rowlabel">Tariff</div>
          {slots.map((s) => {
            const band = tariffBand(new Date(s.t), tariff)
            return <div key={`b${s.t}`} className={`timeline-band band-${band ?? 'flat'}`} title={band === 'OFF_PEAK' ? 'Off-peak' : band === 'PEAK' ? 'Peak' : ''} />
          })}
        </div>
      </div>

      <div className="timeline-legend">
        <span><i className="swatch cell-CHARGE" /> Charge</span>
        <span><i className="swatch cell-HOLD" /> Hold</span>
        <span><i className="swatch cell-DISCHARGE" /> Use battery</span>
        <span><i className="swatch swatch-window" /> Your window</span>
        {tariff?.tariff_type === 'TIME_OF_USE' && (
          <>
            <span><i className="swatch band-OFF_PEAK" /> Off-peak{tariff.off_peak_rate_p != null && ` ${tariff.off_peak_rate_p.toFixed(2)}p`}</span>
            <span><i className="swatch band-PEAK" /> Peak{tariff.peak_rate_p != null && ` ${tariff.peak_rate_p.toFixed(2)}p`}</span>
          </>
        )}
      </div>
    </div>
  )
}

function Row({ label, slots, actionOf }: { label: string; slots: PreviewSlot[]; actionOf: (s: PreviewSlot) => string }) {
  return (
    <>
      <div className="timeline-rowlabel">{label}</div>
      {slots.map((s) => {
        const a = actionOf(s)
        const classes = [
          'timeline-cell', `cell-${a}`,
          s.s === 'CUSTOMER_WINDOW' ? 'cell-window' : '',
          s.inDraft ? 'cell-draft' : '',
        ].filter(Boolean).join(' ')
        return (
          <div
            key={`${label}${s.t}`}
            className={classes}
            title={`${fmtDay(s.t)} ${fmtTime(s.t)} — ${ACTION_LABEL[a as keyof typeof ACTION_LABEL] ?? a}`}
          />
        )
      })}
    </>
  )
}
